/** @jsxImportSource @emotion/react */

import { useEffect, useCallback } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { IconButton } from '@mui/material'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import Table, { FooterWrapper, HeaderWrapper, PaginationPage, PaginationTake } from 'components/Table'
import { Search } from 'components/Input'
import Select from 'components/Select'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { searchTemplateQuestion_searchTemplateQuestion_items } from '__generatedTypes__/searchTemplateQuestion'
import { searchQuestionCategory_searchQuestionCategory_items as QuestionCategory } from '__generatedTypes__/searchQuestionCategory'
import Loader from 'components/Loader'
import { useQuestionFilters } from 'pagesNew/Questions/List/useQuestionFilters'
import { QUESTION_CATEGORY_PARAM } from '../List/consts'
import { useInitialCategoryFromUrl } from '../useInitialCategoryFromUrl'
import { useQuestionTemplates } from './useQuestions'
import * as classes from './styles'

export const QuestionTemplates = () => {
  const { getLink } = useLink()
  const [, setSearchParams] = useSearchParams()
  const {
    categoryDisplayValue,
    setCategoryDisplayValue,
    loadingInitialCategory,
    initialCategoryFilter,
    errorInitialCategory,
  } = useInitialCategoryFromUrl()

  const questionTemplatesState = useQuestionTemplates(initialCategoryFilter)
  const { items, count, loading, refetch, pagination, search, onFilterChange } = questionTemplatesState

  const { questionCategory: categoryFilter } = useQuestionFilters()

  useEffect(() => {
    refetch()
  }, [refetch])

  const handleCategoryChange = useCallback(
    (value: QuestionCategory | null) => {
      onFilterChange('questionCategory', value ?? undefined)
      setCategoryDisplayValue(value)
      setSearchParams((prev) => {
        if (value) {
          prev.set(QUESTION_CATEGORY_PARAM, value.id)
        } else {
          prev.delete(QUESTION_CATEGORY_PARAM)
        }
        return prev
      })
    },
    [onFilterChange, setCategoryDisplayValue, setSearchParams]
  )

  useEffect(() => {
    if (errorInitialCategory) {
      handleCategoryChange(null)
    }
  }, [errorInitialCategory])

  if (!questionTemplatesState.loaded || loadingInitialCategory) {
    return <Loader />
  }

  return (
    <div css={classes.container}>
      <div css={classes.toolbarWrapper}>
        <Search {...search} />
      </div>
      <HeaderWrapper css={classes.headerWrapper}>
        <div css={classes.filtersWrapper}>
          <Select<QuestionCategory>
            items={categoryFilter.items}
            value={categoryDisplayValue}
            onChange={handleCategoryChange}
            placeholder="All Question Categories"
            onInputChange={categoryFilter.search}
            loading={categoryFilter.loading}
            displaySelectedItemsFirst
            searchable
          />
        </div>
        <PaginationPage totalCount={count ?? 0} {...pagination} />
      </HeaderWrapper>
      <Table
        css={classes.tableWrapper}
        columns={[
          {
            title: 'Template Name',
            renderCell: (questionTemplate) => (
              <div css={classes.titleWrapper}>
                <div>{questionTemplate.templateName}</div>
              </div>
            ),
            flex: 2,
          },
          {
            title: 'Date Added',
            renderCell: (questionTemplate) => new Date(questionTemplate.createdAt).toLocaleDateString(),
          },
          {
            title: '',
            renderCell: (questionTemplate) => (
              <Link to={getLink(cmsRoutes.QUESTION_TEMPLATE_EDIT, { questionTemplateId: questionTemplate.id })}>
                <IconButton size="small" css={classes.action} disabled={loading}>
                  <EditIcon />
                </IconButton>
              </Link>
            ),
            flex: '0 1 64px',
          },
        ]}
        items={items as searchTemplateQuestion_searchTemplateQuestion_items[]}
        wrapperMinWidth={500}
      />
      <FooterWrapper>
        <PaginationTake {...pagination} css={classes.paginationTake} />
      </FooterWrapper>
    </div>
  )
}
